import { useCallback, useEffect, useState } from "react";
import { FaGoogle } from "react-icons/fa";
import { fetchDocStorageConfig } from "../../../api/adm";
import { fetchDriveHeaderStatus } from "../../../api/cadDocAutomation";
import { useAuth } from "../../../context/AuthContext";
import { userHasAnyAuth } from "../../../utils/userRoles";
import "./DriveConnectedBadge.css";

/** Disparado após ligar/desligar Drive (Repositório de Documentos) para actualizar o header. */
export const DRIVE_STATUS_CHANGED_EVENT = "spm-drive-status-changed";

/**
 * @param {{ flDriveConnected?: string } | void} [detail]
 *   Opcional: flag da companhia para actualizar o bullet antes do refetch.
 */
export function notifyDriveStatusChanged(detail) {
  window.dispatchEvent(
    new CustomEvent(DRIVE_STATUS_CHANGED_EVENT, { detail: detail || null })
  );
}

function yn(v) {
  return String(v || "N").trim().toUpperCase() === "Y";
}

const PLATFORM_TOOLTIP = "Conectado ao Drive da Plataforma";

function driveTooltip(connected, providerCd, { platform = false } = {}) {
  if (!connected) return "Drive desligado";
  if (platform) return PLATFORM_TOOLTIP;
  if (providerCd === "MICROSOFT_GRAPH") return "Microsoft OneDrive ligado";
  if (providerCd === "GOOGLE_DRIVE") return "Google Drive ligado";
  return "Drive ligado";
}

function badgeState({ connected, providerCd, platform }) {
  if (!connected) return null;
  return {
    connected: true,
    platform: Boolean(platform),
    isGoogle: !platform && providerCd === "GOOGLE_DRIVE",
    title: driveTooltip(true, providerCd, { platform }),
  };
}

/**
 * Bullet Drive na barra superior (empresa activa).
 * - Plataforma (Ativo=Não + ligado): letra «P»
 * - Drive próprio Google: ícone Google
 * - Outro: «D»
 * DESLIGADO: ícone oculto.
 *
 * Em contrato: `fl_drive_connected`; plataforma se `fl_using_adm_fallback=Y`.
 * Portal ADM (sem empresa): Drive da plataforma.
 */
export function DriveConnectedBadge() {
  const { user, activeCompanyId } = useAuth();
  const [state, setState] = useState(null);

  const canReadAdm = userHasAnyAuth(user, [
    "ADM_DOC_STORAGE_RD",
    "ADM_DOC_STORAGE_WR",
  ]);

  const refresh = useCallback(async () => {
    if (activeCompanyId == null && !canReadAdm) {
      setState(null);
      return;
    }

    let companyConnected = false;
    let usingFallback = false;
    let platformDriveConnected = false;
    let sawCad = false;
    let providerCd = "GOOGLE_DRIVE";

    if (activeCompanyId != null) {
      try {
        const st = await fetchDriveHeaderStatus(activeCompanyId);
        sawCad = true;
        companyConnected = yn(st?.flDriveConnected);
        usingFallback = yn(st?.flUsingAdmFallback);
        const cd = String(st?.cdDriveProvider || "")
          .trim()
          .toUpperCase();
        if (cd) providerCd = cd;
      } catch {
        /* 401/403/rede — ADM pode cobrir fora de contrato */
      }
    }

    // Portal ADM (sem companhia): Drive da plataforma via /adm/doc-storage.
    if (activeCompanyId == null && canReadAdm) {
      try {
        const adm = await fetchDocStorageConfig();
        platformDriveConnected = yn(
          adm?.fl_drive_connected || adm?.flDriveConnected
        );
        const cd = String(adm?.cd_provider || adm?.cdProvider || "")
          .trim()
          .toUpperCase();
        if (cd) providerCd = cd;
      } catch {
        /* sem ADM_DOC_STORAGE_* */
      }
    }

    if (!sawCad && activeCompanyId == null && !canReadAdm) {
      setState(null);
      return;
    }

    if (activeCompanyId != null) {
      setState(
        badgeState({
          connected: companyConnected,
          providerCd,
          platform: usingFallback,
        })
      );
      return;
    }

    setState(
      badgeState({
        connected: platformDriveConnected,
        providerCd,
        platform: true,
      })
    );
  }, [activeCompanyId, canReadAdm]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onOauth = (ev) => {
      const d = ev?.data;
      if (!d || d.type !== "spm-drive-oauth") return;
      void refresh();
    };
    const onStatus = (ev) => {
      const d = ev?.detail;
      // Optimistic: chip Repositório / ADM já sabe o novo estado efectivo.
      if (
        d &&
        (d.flDriveConnected != null ||
          d.fl_drive_connected != null ||
          d.flPlatformDriveConnected != null ||
          d.fl_platform_drive_connected != null)
      ) {
        let connected;
        let platform = false;
        if (activeCompanyId != null) {
          connected = yn(d.flDriveConnected ?? d.fl_drive_connected);
          platform = yn(
            d.flUsingAdmFallback ?? d.fl_using_adm_fallback ?? "N"
          );
        } else {
          connected = yn(
            d.flDriveConnected ??
              d.fl_drive_connected ??
              d.flPlatformDriveConnected ??
              d.fl_platform_drive_connected
          );
          platform = true;
        }
        setState(
          badgeState({
            connected,
            providerCd: "GOOGLE_DRIVE",
            platform,
          })
        );
      }
      void refresh();
    };
    window.addEventListener("message", onOauth);
    window.addEventListener(DRIVE_STATUS_CHANGED_EVENT, onStatus);
    return () => {
      window.removeEventListener("message", onOauth);
      window.removeEventListener(DRIVE_STATUS_CHANGED_EVENT, onStatus);
    };
  }, [refresh, activeCompanyId]);

  // DESLIGADO: ícone oculto por completo (não círculo cinza).
  if (!state?.connected) return null;

  return (
    <span
      className={
        state.platform
          ? "app-header-drive-badge app-header-drive-badge--on app-header-drive-badge--platform"
          : "app-header-drive-badge app-header-drive-badge--on"
      }
      role="status"
      title={state.title}
      aria-label={state.title}
    >
      {state.platform ? (
        <span className="app-header-drive-badge__letter" aria-hidden>
          P
        </span>
      ) : state.isGoogle ? (
        <FaGoogle className="app-header-drive-badge__icon" aria-hidden />
      ) : (
        <span className="app-header-drive-badge__letter" aria-hidden>
          D
        </span>
      )}
    </span>
  );
}

export default DriveConnectedBadge;
